/**
 * Analytics & Finance (Analytics & Intelligence spec). Tabbed:
 *   • Финансии    — P&L trend, revenue-by-kind, income/expense overviews (P0, reused)
 *   • Предвидувања — business-health composite, transparent churn-risk score, member
 *                    segments (RFM), renewal forecast, most-valuable-plan/LTV (P2)
 *   • Задржување  — MRR/movement, churn/cohort/tenure/freeze, at-risk + revenue-at-risk, ghost (P1)
 *   • Планови     — plan performance (units vs revenue vs retention vs LTV) + pack burn-down;
 *                   retail product best/worst sellers (units, revenue, margin)
 *   • Привлекување — acquisition channel quality + AR aging
 *   • Часови      — class fill/no-show + waitlist demand
 * The Финансии tab is the pre-existing P0 report, unchanged.
 */
import { getProfitLoss, getRevenueByKind } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedBarChart, seriesColor, TrendChart } from '../../components/charts/index.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { ChartCard, PageHeader } from '../../components/ui/Layout.js';
import { Money, useFormatters, useLocale } from '../../components/ui/Money.js';
import { Tabs } from '../../components/ui/Tabs.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import { AcquisitionAnalytics } from './AcquisitionAnalytics.js';
import { ClassAnalytics } from './ClassAnalytics.js';
import { ExpenseOverview } from './ExpenseOverview.js';
import { IncomeOverview } from './IncomeOverview.js';
import { IntelligenceAnalytics } from './IntelligenceAnalytics.js';
import { PlanPerformance } from './PlanPerformance.js';
import { ProductPerformance } from './ProductPerformance.js';
import { RetentionAnalytics } from './RetentionAnalytics.js';

type Tab = 'financial' | 'retention' | 'plans' | 'acquisition' | 'classes' | 'intelligence';

function FinancialTab({ tenantId }: { tenantId: string }): ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const fmt = useFormatters();

  const pl = useQuery({
    queryKey: ['profit_loss', tenantId],
    queryFn: () => getProfitLoss(supabase, tenantId),
    enabled: !!tenantId,
  });
  const rbk = useQuery({
    queryKey: ['revenue_by_kind', tenantId],
    queryFn: () => getRevenueByKind(supabase, tenantId),
    enabled: !!tenantId,
  });

  const months = useMemo(() => [...(pl.data ?? [])].reverse(), [pl.data]);
  const current = pl.data?.[0];

  const trendData = useMemo(
    () =>
      months.slice(-8).map((m) => ({
        label: fmt.date(m.month_bucket, { month: 'short' }),
        [t('analytics.income')]: Math.round(m.income / 100),
        [t('analytics.expenses')]: Math.round(m.expense / 100),
        [t('analytics.profit')]: Math.round(m.profit / 100),
      })),
    [months, fmt, t],
  );

  const kindData = useMemo(() => {
    const agg = new Map<string, number>();
    for (const r of rbk.data ?? []) agg.set(r.kind, (agg.get(r.kind) ?? 0) + r.total);
    return [...agg.entries()].map(([kind, total], i) => ({
      label: t(`analytics.kind.${kind}`, { defaultValue: kind }),
      value: Math.round(total / 100),
      color: seriesColor(i),
    }));
  }, [rbk.data, t]);

  return (
    <div className="grid gap-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label={t('analytics.income')}
          value={<Money minor={current?.income ?? 0} className="text-3xl" />}
          tone="success"
          loading={pl.isLoading}
        />
        <KpiCard
          label={t('analytics.expenses')}
          value={<Money minor={current?.expense ?? 0} className="text-3xl" />}
          tone="warning"
          loading={pl.isLoading}
        />
        <KpiCard
          label={t('analytics.profit')}
          value={<Money minor={current?.profit ?? 0} className="text-3xl" />}
          tone="primary"
          loading={pl.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ChartCard
          title={t('analytics.profitLoss')}
          subtitle={t('analytics.last8Months')}
          loading={pl.isLoading}
          empty={trendData.length === 0 ? t('common.noData') : undefined}
          className="xl:col-span-2"
        >
          <TrendChart
            data={trendData}
            series={[
              { key: t('analytics.income'), name: t('analytics.income'), color: '#22C55E' },
              { key: t('analytics.expenses'), name: t('analytics.expenses'), color: '#F59E0B' },
              {
                key: t('analytics.profit'),
                name: t('analytics.profit'),
                color: '#F97316',
                area: false,
              },
            ]}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>
        <ChartCard
          title={t('analytics.revenueByKind')}
          subtitle={t('analytics.allTime')}
          loading={rbk.isLoading}
          empty={kindData.length === 0 ? t('common.noData') : undefined}
        >
          <RankedBarChart
            data={kindData}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>
      </div>

      <IncomeOverview tenantId={tenantId} />
      <ExpenseOverview tenantId={tenantId} />
    </div>
  );
}

export function AnalyticsPage(): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const [tab, setTab] = useState<Tab>('financial');

  return (
    <div className="grid gap-5">
      <PageHeader title={t('nav.analytics')} subtitle={t('analytics.financeSubtitle')} />

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        items={[
          { value: 'financial', label: t('analytics.tabs.financial') },
          { value: 'intelligence', label: t('analytics.tabs.intelligence') },
          { value: 'retention', label: t('analytics.tabs.retention') },
          { value: 'plans', label: t('analytics.tabs.plans') },
          { value: 'acquisition', label: t('analytics.tabs.acquisition') },
          { value: 'classes', label: t('analytics.tabs.classes') },
        ]}
      />

      {tab === 'financial' ? <FinancialTab tenantId={tenantId} /> : null}
      {tab === 'intelligence' ? <IntelligenceAnalytics /> : null}
      {tab === 'retention' ? <RetentionAnalytics /> : null}
      {tab === 'plans' ? (
        <div className="grid gap-5">
          <PlanPerformance />
          <ProductPerformance />
        </div>
      ) : null}
      {tab === 'acquisition' ? <AcquisitionAnalytics /> : null}
      {tab === 'classes' ? <ClassAnalytics /> : null}
    </div>
  );
}
